import * as HLF from "html-living-framework";

import { Application } from "Application";
import { DEFAULT_TRANSITION, LOAD_INTERSTITIAL, STATE_CITY_SELECT } from "Data/Constants";
import { GameConfig, LocationIDs } from "Data/GameConfig";
import type { HidingSpotID, ItemID, LocationID } from "Data/GameConfig";
import * as Topics from "Data/Topics";
import { ArrayUtils } from "html-living-framework/lib/Utils";
import type { IHiddenItemConfig } from "Interfaces/IHiddenItemConfig";
import type { IHidingSpotState } from "Interfaces/IHidingSpotState";
import type { IItemState } from "Interfaces/IItemState";
import type { ILevelState } from "Interfaces/ILevelState";
import type { ILocationConfig } from "Interfaces/ILocationConfig";
import type { INarrativeConfig } from "Interfaces/INarrativeConfig";
import type { INarrativeVariationConfig } from "Interfaces/INarrativeVariationConfig";
import { GameExterior } from "States/GameExterior";
import { GameInteriorGiveQuest } from "States/GameInteriorGiveQuest";
import { GameInteriorReturnItems } from "States/GameInteriorReturnItems";

export class GameState {

    public static get Instance(): GameState {
        if (GameState._Instance === undefined) {
            GameState._Instance = new GameState();
            GameState._Instance.Load();
            // GameState._Instance.Reset();
        }
        return GameState._Instance;
    }

// #region public instance variables
    public get currentLevelConfig() {
        const c = GameConfig.locations[this._currentLocation];
        const s = this._locations[this._currentLocation];
        return c.narratives[s!.narrative].variations[s!.narrativeVariation];
    }

    public get onboarding() {
        return this._onboarding;
    }
// #endregion

private static _Instance: GameState;

// #region backing fields for public accessors
    protected _locations: Partial<{ [K in LocationID]: ILevelState }> = {};
    protected _onboarding: {complete: boolean} = {complete: false};
    protected _currentLocation: LocationID = LocationIDs[0];
// #endregion

// #region private static methods (helpers)
    /** Returns an empty state (no items, no hiding spots, no payoff, etc) */
    private static CreateEmptyState(id: LocationID): ILevelState {
        return {
            id,
            narrative: -1,
            narrativeVariation: -1,
            hidingSpots: {},
            items: {},
            payoff: "",
            allItemsFound: false,
            allItemsReturned: false,
            completed: false,
            allLevelsCompleted: false,
        };
    }

    private static AssignItemsToHidingSpots(
        state: ILevelState,
        config: ILocationConfig,
        hiddenitems: IHiddenItemConfig[],
    ) {
        state.hidingSpots = {};
        for (const id of Object.keys(config.hidingspots) as HidingSpotID[]) {
            state.hidingSpots[id] = {
                id,
                searched: false,
            };
        }

        hiddenitems.forEach((i: IHiddenItemConfig) => {
            const hidingSpotID = (typeof(i.hidingspot) === "string" ? i.hidingspot : i.hidingspot.id) as HidingSpotID;
            const spot = state.hidingSpots[hidingSpotID];

            if (spot !== undefined) {
                if (typeof(i.item) === "string") {
                    spot.itemID = i.item as ItemID;
                }
                else {
                    spot.itemID = i.item.id as ItemID;
                }
            }
            else {
                console.error("couldn't find hiding spot with id " + i.hidingspot);
            }
        });
    }

    /** Populates state.items */
    private static SelectItemsForLevel(state: ILevelState, levelConfig: INarrativeVariationConfig) {
        const hiddenitems = levelConfig.hiddenitems;
        state.items = {};
        state.allItemsFound = false;
        state.allItemsReturned = false;
        hiddenitems.forEach((config: IHiddenItemConfig) => {
            const id = (typeof(config.item) === "string" ? config.item : config.item.id) as ItemID;
            state.items[id] = {
                id,
                found: false,
                returned: false,
            };
        });
    }

    /** Populates state.narrative and state.narrativeVariation */
    private static SelectFirstNarrativeVariation(state: ILevelState, config: Readonly<ILocationConfig>) {
        state.narrative = 0;
        state.narrativeVariation = 0;
        GameState.Populate(state, config);
    }

    private static SelectRandomNarrativeVariation(state: ILevelState, config: Readonly<ILocationConfig>) {
        const choices = [];
        for (let n = 0; n < config.narratives.length; n++) {
            for (let v = 0; v < config.narratives[n].variations.length; v++ ) {
                if (n === state.narrative && v === state.narrativeVariation) {
                    // skip so we don't play the same level twice in a row
                }
                else {
                    choices.push({n, v});
                }
            }
        }
        const next = ArrayUtils.random(choices);
        state.narrative = next.n;
        state.narrativeVariation = next.v;
        GameState.Populate(state, config);
    }

    private static SelectNextNarrativeVariation(
        state: ILevelState,
        config: Readonly<ILocationConfig>,
    ): boolean {
        // increment the variable (without checking if it would be valid to do so)
        state.narrativeVariation++;

        // then we check if that puts us out of range in the inner array
        if (state.narrativeVariation >= config.narratives[state.narrative].variations.length) {
            // first variation of next narrative
            state.narrative++;
            state.narrativeVariation = 0;
        }
        else {
            // next variation of same narrative
        }

        // check for out of range of the outer array
        if (state.narrative >= config.narratives.length) {
            state.allLevelsCompleted = true;
            return false;
        }
        GameState.Populate(state, config);
        return true;
    }

    private static RestartCurrentNarrativeVariation(state: ILevelState, config: Readonly<ILocationConfig>) {
        GameState.Populate(state, config);
    }

    private static SetPayoff(state: ILevelState, levelConfig: Readonly<INarrativeConfig>) {
        state.payoff = levelConfig.payoff;
        state.completed = false;
    }

    /** Populate the ILevelState with all the necessary data */
    private static Populate(state: ILevelState, config: Readonly<ILocationConfig>) {
        const levelConfig = config.narratives[state.narrative].variations[state.narrativeVariation];
        GameState.SetPayoff(state, config.narratives[state.narrative]);
        GameState.SelectItemsForLevel(state, levelConfig);
        GameState.AssignItemsToHidingSpots(state, config, levelConfig.hiddenitems);
    }
// #endregion

    public CompleteLevel(pID: LocationID = this._currentLocation) {
        const state = this._locations[pID];
        if (state === undefined) {
            throw new Error(`Cannot find location with ID "${pID}"`);
        }
        if (!state.completed) {
            state.completed = true;
            if (!this._onboarding.complete) {
                this._onboarding.complete = true;
                Application.instance.analytics?.event("tutorial_complete", "lunas-adventures");
            }
            PubSub.publishSync(Topics.LEVEL_COMPLETE, state);
            Application.instance.analytics?.event(
                [state.id, state.narrative, state.narrativeVariation].join("_"),
                "LevelCompleted",
            );
            Application.instance.analytics?.event(
                "level_up", "lunas-adventures", [state.id, state.narrative, state.narrativeVariation].join("_"),
            );

        }
        this.Save();
    }

    public ReturnItem(pID: ItemID) {
        const state = this._locations[this._currentLocation]!;
        const item = state.items[pID];
        if (item === undefined) {
            throw new Error(`Cannot find item with ID "${pID}"`);
        }

        if (!item.returned) {
            item.returned = true;
            PubSub.publishSync(Topics.ITEM_RETURNED, item);
            const count = Object.values(state.items).reduce((n, i) => {
                if (i.returned) { return n + 1; }
                else { return n; }
            }, 0 );
            Application.instance.analytics?.event(
                [state.id, state.narrative, state.narrativeVariation].join("_"),
                "ItemReturned",
                item.id,
                count,
            );
            Application.instance.analytics?.event(
                "select_content", "lunas-adventures",
                [state.id, item.id].join("_"),
            );
        }

        const allItemsReturned = Object.values(state.items).every((i) => i !== undefined && i.returned);
        if (allItemsReturned) {
            if (!state.allItemsReturned) {
                state.allItemsReturned = true;
                PubSub.publishSync(Topics.ALL_ITEMS_RETURNED, state);
                Application.instance.analytics?.event(
                    [state.id, state.narrative, state.narrativeVariation].join("_"),
                    "AllItemsReturned",
                    state.payoff,
                );
                GameState.Instance.CompleteLevel(state.id);
            }
        }
    }

// #region public methods (state changes)
    // functions take in only ids, but pubsub events carry whole objects
    public SearchHidingSpot(pID: HidingSpotID) {
        const state = this._locations[this._currentLocation]!;
        let spot: IHidingSpotState | undefined;
        let item: IItemState | undefined;

        spot = state.hidingSpots[pID];

        if (spot === undefined) {
            throw new Error(`Cannot find hiding spot with ID "${pID}"`);
        }

        if (spot.itemID !== undefined) {
            item = state.items[spot.itemID];
        }

        if (!spot.searched) {
            spot.searched = true;
        }
        PubSub.publishSync(Topics.HIDING_SPOT_SEARCHED, spot); // send the searched event even on repeated searches

        Application.instance.analytics?.event(
            [state.id, state.narrative, state.narrativeVariation].join("_"),
            "HidingSpotSearched",
            spot.id,
        );

        Application.instance.analytics?.event(
            "select_content", "lunas-adventures",
            [state.id, spot.id].join("_"),
        );

        if (item !== undefined) {
            if (!item.found) {
                item.found = true;
                PubSub.publishSync(Topics.ITEM_FOUND, item); // only send the found event once per item
                const count = Object.values(state.items).reduce((n, i) => {
                    if (i.found) { return n + 1; }
                    else { return n; }
                }, 0 );
                Application.instance.analytics?.event(
                    [state.id, state.narrative, state.narrativeVariation].join("_"),
                    "ItemFound",
                    item.id,
                    count,
                );
                Application.instance.analytics?.event(
                    "select_content", "lunas-adventures",
                    [state.id, item.id].join("_"),
                );
            }
            const allItemsFound = Object.values(state.items).every((i) => i !== undefined && i.found);
            if (allItemsFound) {
                if (!state.allItemsFound) {
                    state.allItemsFound = true;
                    PubSub.publishSync(Topics.ALL_ITEMS_FOUND, state);
                    Application.instance.analytics?.event(
                        [state.id, state.narrative, state.narrativeVariation].join("_"),
                        "AllItemsFound",
                    );
                }
            }
        }
    }

    public GoToExterior(pId: LocationID = this._currentLocation) {
        const state = this._locations[pId] ?? GameState.CreateEmptyState(pId);
        const config = GameConfig.locations[state.id];
        if (state.completed) {
            // go directly from returnitems to exterior (skip give quest)
            if (state.allLevelsCompleted) {
                // all missions in location complete
                // choose a random level from the same location
                GameState.SelectRandomNarrativeVariation(state, config);
            }
            else {
                const nextLevelExists = GameState.SelectNextNarrativeVariation(state, config);

                if (!nextLevelExists) {
                    // finished the last level in location
                    // choose a random level from the same location
                    GameState.SelectRandomNarrativeVariation(state, config);
                }
                else {
                    // go to next level from the same location
                }
            }
        }
        else if (Object.values(state.items).find((i) => i.found)) {
            Application.instance.analytics?.event(
                [pId, state.narrative, state.narrativeVariation].join("_"),
                "ResumeLevel",
            );
        }
        else {
            Application.instance.analytics?.event(
                [pId, state.narrative, state.narrativeVariation].join("_"),
                "StartLevel",
            );
        }

        this._currentLocation = pId;
        this._locations[pId] = state;

        PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken({
            id: GameExterior.GetIdForState(pId, state.narrative, state.narrativeVariation),
            data: state,
        }, LOAD_INTERSTITIAL, ...DEFAULT_TRANSITION));
    }

    public GoToInterior(pId?: LocationID): void {
        let wasContinue = false;
        let firstTime = false;
        if (pId === undefined) {
            pId = this._currentLocation;
            wasContinue = true;
        }
        const state = this._locations[pId] ?? GameState.CreateEmptyState(pId);
        const config = GameConfig.locations[state.id];

        // sometimes it's easier to use -1 instead of null or undefined
        if (state.narrative === -1 || state.narrativeVariation === -1) {
            // first time visiting this location
            GameState.SelectFirstNarrativeVariation(state, config);
            firstTime = true;
        }
        else if (state.completed) {
            if (state.allLevelsCompleted) {
                // all missions in location complete
                // choose a random level from the same location
                GameState.SelectRandomNarrativeVariation(state, config);
            }
            else {
                const nextLevelExists = GameState.SelectNextNarrativeVariation(state, config);

                if (!nextLevelExists) {
                    // just finished the last level in location
                    // choose a random level from the same location
                    GameState.SelectRandomNarrativeVariation(state, config);
                }
                else {
                    // go to next level from the same location
                }
            }
        }

        this._currentLocation = pId;
        this._locations[pId] = state;

        if (state.allItemsFound) {
            Application.instance.analytics?.event(
                [pId, state.narrative, state.narrativeVariation].join("_"),
                "ReturnItems",
            );
            PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken({
                id: GameInteriorReturnItems.GetIdForState(pId, state.narrative, state.narrativeVariation),
                data: state,
            }, LOAD_INTERSTITIAL, ...DEFAULT_TRANSITION));
        }
        else {
            Application.instance.analytics?.event(
                [pId, state.narrative, state.narrativeVariation].join("_"),
                "RecieveQuest",
            );
            Application.instance.analytics?.event(
                "startLevel", "lunas-adventures", [pId, state.narrative, state.narrativeVariation].join("_"),
            );
            PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken({
                id: GameInteriorGiveQuest.GetIdForState(pId, state.narrative, state.narrativeVariation),
                data: {...state, wasContinue, firstTime},
            }, LOAD_INTERSTITIAL, ...DEFAULT_TRANSITION));
        }

    }

    public GoToLevelSelect() {
        const state = this._locations[this._currentLocation];
        if (state) {
            Application.instance.analytics?.event(
                [this._currentLocation, state.narrative, state.narrativeVariation].join("_"),
                "ReturnToLevelSelect",
            );
            Application.instance.analytics?.event(
                "endLevel", "lunas-adventures", [state.id, state.narrative, state.narrativeVariation].join("_"),
            );
        }
        PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken(
            STATE_CITY_SELECT,
            LOAD_INTERSTITIAL,
            ...DEFAULT_TRANSITION,
        ));
    }

    public DEBUG_ForceState(
        id: LocationID,
        narrative = -1,
        narrativeVariation = -1,
    ) {
        const state = this._locations[id] ?? GameState.CreateEmptyState(id);
        const config = GameConfig.locations[state.id];
        state.narrative = narrative;
        state.narrativeVariation = narrativeVariation;
        GameState.Populate(state, config);
        this._currentLocation = id;
        this._locations[id] = state;
        this.Save();
        return state;
    }

// #endregion

// #region persistence
    protected Save() {
        Application.instance.saveManager.storeString("locations", JSON.stringify(this._locations), 1);
        Application.instance.saveManager.storeString("onboarding", JSON.stringify(this._onboarding), 1);
    }

    protected Load() {
        try {
            this._locations = JSON.parse(
                Application.instance.saveManager.readString("locations", JSON.stringify(this._locations), 1),
            );
            this._onboarding = JSON.parse(
                Application.instance.saveManager.readString("onboarding", JSON.stringify(this._onboarding), 1),
            );
        }
        catch (e) {
            console.error("Error loading saved state. Resetting.", e);
            this.Reset();
        }
    }

    protected Reset() {
        this._locations = {};
        this._onboarding = {complete: false};
        this.Save();
    }
// #endregion
}
